import { ALL_TILES, poolFor } from '../facts';
import type { Fact, Operation, Tile } from '../facts';
import { shuffle } from '../rng';
import type { Rng } from '../rng';

export type Relation = '>' | '=' | '<';
export type Mode = 'mul' | 'div' | 'mixed';

const RELATIONS: readonly Relation[] = ['>', '=', '<'];

function relationOf(left: Fact, right: Fact): Relation {
  if (left.answer > right.answer) return '>';
  if (left.answer < right.answer) return '<';
  return '=';
}

function other(op: Operation): Operation {
  return op === 'mul' ? 'div' : 'mul';
}

/** Источники партнёра по порядку. Своя операция исчерпывается раньше
 *  чужой, и в одиночных режимах чужая не используется вовсе: §9 разрешает
 *  смешивать операции только в смешанном режиме (раздел 5.6). */
export function partnerSources(
  tiles: readonly Tile[],
  mode: Mode,
  op: Operation,
): Fact[][] {
  const sources = [poolFor(tiles, op), poolFor(ALL_TILES, op)];
  if (mode === 'mixed') {
    sources.push(poolFor(tiles, other(op)), poolFor(ALL_TILES, other(op)));
  }
  return sources;
}

export function reachableRelations(left: Fact, sources: readonly Fact[][]): Relation[] {
  const found = new Set<Relation>();
  for (const source of sources) {
    for (const candidate of source) {
      if (candidate.id === left.id) continue;
      found.add(relationOf(left, candidate));
    }
  }
  return RELATIONS.filter((r) => found.has(r));
}

/** Совместное распределение: исходы назначаются после того, как левые
 *  выражения уже выданы. Проверки «достижимо для пула» недостаточно —
 *  в {×2, ×4} равенство достижимо для пула и ни для одного из шести
 *  выпавших фактов (раздел 5.6). Перебор по 3^6 = 729 вариантам. */
/** Предел числа сравнений в одной раздаче.
 *
 *  При десяти перебор занимает 59 049 узлов, при одиннадцати — 177 147:
 *  оба мгновенны. Реальная тренировка из двадцати заданий даёт шесть
 *  сравнений, то есть десять вмещает тренировку примерно на тридцать три
 *  задания — с запасом к нынешним двадцати и к пределу `MAX_TASK_COUNT`,
 *  равному тридцати. */
const MAX_LEFTS = 10;

export function assignRelations(
  lefts: readonly Fact[],
  sourcesPerLeft: readonly (readonly Fact[][])[],
  rng: Rng,
): Relation[] {
  /** Ближний предел на размер перебора.
   *
   *  `walk` — исчерпывающий обход 3^n назначений. При шести сравнениях это
   *  729 узлов, то есть мгновенно; при четырнадцати — пять миллионов, и
   *  приложение ЗАМИРАЕТ. Не падает, не ошибается — замирает, а это худший
   *  из возможных исходов: ребёнок видит застывший экран, наружу не уходит
   *  ничего.
   *
   *  Найдено мутационным проходом: вывернутое условие в `buildSession`
   *  подняло число сравнений с шести до четырнадцати, и прогон, который
   *  идёт полторы секунды, шёл десять минут, пока его не сняли.
   *
   *  Предел на это УЖЕ ЕСТЬ — `MAX_TASK_COUNT` в `session.ts`, — но стоит
   *  он на входе `buildSession`, то есть в другом модуле и на другой
   *  величине. Функция, которая платит цену, должна знать её сама: любой
   *  новый вызывающий (повтор ошибок, будущий вид задания, тест) обойдёт
   *  тот предел, не заметив. */
  /** Массивы параллельные, и их рассогласование — не мелочь: лишние
   *  источники молча игнорировались бы, а недостающие давали бы случайное
   *  исключение где-то внутри перебора вместо внятного текста здесь. */
  if (lefts.length !== sourcesPerLeft.length) {
    throw new Error(
      `assignRelations: списков разной длины (${lefts.length} левых, ${sourcesPerLeft.length} источников)`,
    );
  }
  if (lefts.length > MAX_LEFTS) {
    throw new Error(
      `assignRelations: ${lefts.length} сравнений — перебор 3^n не окупается, предел ${MAX_LEFTS}`,
    );
  }

  const options = lefts.map((left, i) => reachableRelations(left, sourcesPerLeft[i]));
  for (const [i, list] of options.entries()) {
    if (list.length === 0) throw new Error(`assignRelations: нет исходов для ${lefts[i].id}`);
  }

  let best: Relation[] | null = null;
  let bestSpread = Number.POSITIVE_INFINITY;

  const walk = (index: number, acc: Relation[]) => {
    if (index === lefts.length) {
      const counts = RELATIONS.map((r) => acc.filter((x) => x === r).length);
      const spread = Math.max(...counts) - Math.min(...counts);
      if (spread < bestSpread) {
        bestSpread = spread;
        best = [...acc];
      }
      return;
    }
    for (const relation of shuffle(options[index], rng)) {
      acc.push(relation);
      walk(index + 1, acc);
      acc.pop();
      if (bestSpread === 0) return;
    }
  };
  walk(0, []);

  if (!best) throw new Error('assignRelations: не найдено ни одного назначения');
  return best;
}

export function findPartner(
  left: Fact,
  relation: Relation,
  sources: readonly Fact[][],
  rng: Rng,
): Fact {
  for (const source of sources) {
    const matches = source.filter(
      (candidate) => candidate.id !== left.id && relationOf(left, candidate) === relation,
    );
    if (matches.length > 0) return shuffle(matches, rng)[0];
  }
  throw new Error(`findPartner: исход ${relation} недостижим для ${left.id}`);
}
